import { COVER_CROP_MIN_GROWING_WINDOW_DAYS, COVER_CROP_MIX_TYPE, IS_PRACTICE_APPLIED_BY_DEFAULT, SOIL_SIGHT_TILLAGE } from "consts";

import { CropYear } from "pages/app/scenario-analysis/types/scenario";
import { growsCoverCrop, pruneEmptyCropYears } from "pages/app/scenario-analysis/utils/cropYearHelpers";

const DAY_MS = 24 * 60 * 60 * 1000;

/** Per-crop inputs for the generation rules, built from `useGetPracticeCrops` data. */
export type CropCalendarInfo = {
  /** PracticeCrop.residue — whether the crop has residues available at all. */
  residueAvailable: boolean;
  medianKwSow: number | null;
  medianKwHarvest: number | null;
};

export type GenerateChangeScenarioResult = {
  /** Every recommendation applied — the values step 3's practice toggles restore rows to. */
  changeYears: CropYear[];
  /** What the adapted scenario is created with: `changeYears` minus the opt-in practices (cover crops). */
  initialChangeYears: CropYear[];
};

/** Builds the generator's per-crop input map from the practice-crops catalogue. */
export const buildCropCalendarInfoById = (
  crops: Array<{ id: string; residue: boolean; medianKwSow?: number | null; medianKwHarvest?: number | null }> | undefined
): Record<string, CropCalendarInfo> =>
  Object.fromEntries(
    (crops ?? []).map((crop) => [
      crop.id,
      { residueAvailable: crop.residue, medianKwSow: crop.medianKwSow ?? null, medianKwHarvest: crop.medianKwHarvest ?? null },
    ])
  );

/**
 * Monday of the given ISO week (UTC) — client mirror of the server's
 * `isoWeekToDate` (moment().year(y).isoWeek(w).startOf("isoWeek")).
 */
export const isoWeekToDate = (year: number, isoWeek: number): Date => {
  const jan4 = new Date(Date.UTC(year, 0, 4));
  const jan4IsoDay = jan4.getUTCDay() || 7;
  const mondayOfWeekOne = Date.UTC(year, 0, 4 - (jan4IsoDay - 1));
  return new Date(mondayOfWeekOne + (isoWeek - 1) * 7 * DAY_MS);
};

type SlotDates = { seedingDate: Date; harvestDate: Date };

// Mirrors SoilSightService.resolveMainDatesPerSlot: `harvestYear` anchors the slot; a
// winter crop (harvest week < sow week) is sown in the year before its harvest.
const computeMainCropDates = (info: CropCalendarInfo, harvestYear: number): SlotDates | null => {
  if (info.medianKwSow == null || info.medianKwHarvest == null) {
    return null;
  }

  const isWinterRollover = info.medianKwHarvest < info.medianKwSow;
  return {
    harvestDate: isoWeekToDate(harvestYear, info.medianKwHarvest),
    seedingDate: isoWeekToDate(harvestYear - (isWinterRollover ? 1 : 0), info.medianKwSow),
  };
};

/**
 * Per-slot seeding/harvest dates, plus a trailing slot wrapping to the first crop so the last row
 * also has a "next seeding" anchor. Mirrors the server's bump-until-after-previous-harvest loop so
 * consecutive main crops never overlap. Slots missing calendar data yield null.
 */
const resolveRotationDates = (rotation: CropYear[], cropInfoById: Record<string, CropCalendarInfo>, baseYear: number): (SlotDates | null)[] => {
  const slotDates: (SlotDates | null)[] = [];
  let previousHarvestDate: Date | null = null;

  for (let slotIndex = 0; slotIndex <= rotation.length; slotIndex += 1) {
    const cropYear = rotation[slotIndex % rotation.length];
    const info = cropYear?.crop ? cropInfoById[cropYear.crop] : undefined;

    let harvestYear = baseYear + slotIndex;
    let dates = info ? computeMainCropDates(info, harvestYear) : null;

    while (info && previousHarvestDate && dates && dates.seedingDate <= previousHarvestDate) {
      harvestYear += 1;
      dates = computeMainCropDates(info, harvestYear);
    }

    previousHarvestDate = dates?.harvestDate ?? null;
    slotDates.push(dates);
  }

  return slotDates;
};

const recommendCoverCrop = (harvestDate: Date, nextSeedingDate: Date): CropYear["coverCropId"] | null => {
  const windowDays = (nextSeedingDate.getTime() - harvestDate.getTime()) / DAY_MS;
  if (windowDays < COVER_CROP_MIN_GROWING_WINDOW_DAYS) {
    return null;
  }

  // Same calendar year as the harvest → summer window; next year → over-winter window.
  // The default type is the multi-species mix.
  const isSummerWindow = nextSeedingDate.getUTCFullYear() === harvestDate.getUTCFullYear();
  return isSummerWindow ? COVER_CROP_MIX_TYPE.SUMMER_MULTI_SPECIES : COVER_CROP_MIX_TYPE.WINTER_MULTI_SPECIES;
};

/**
 * Generation rules, per baseline row: residue removed → left on field; conventional tillage →
 * reduced → no-till; no cover crop → multi-species mix when the harvest-to-next-seeding window is at
 * least 6 weeks (summer vs winter mix by whether that seeding falls in the same calendar year).
 * Row ids are preserved, so step 3 can line the generated rows up with the baseline and the change rows.
 * Cover crops are recommended but not applied to `initialChangeYears` — see IS_PRACTICE_APPLIED_BY_DEFAULT.
 */
export const generateChangeScenario = (
  baselineYears: CropYear[],
  cropInfoById: Record<string, CropCalendarInfo>,
  baseYear: number
): GenerateChangeScenarioResult => {
  const rotation = pruneEmptyCropYears(baselineYears).filter((year) => Boolean(year.crop?.trim()));
  const slotDates = resolveRotationDates(rotation, cropInfoById, baseYear);

  const initialChangeYears: CropYear[] = [];

  const changeYears = rotation.map((year, slotIndex) => {
    const info = cropInfoById[year.crop];
    const changeYear: CropYear = { ...year };

    if (info?.residueAvailable && year.residue === false) {
      changeYear.residue = true;
    }

    if (year.tillageId === SOIL_SIGHT_TILLAGE.CONVENTIONAL) {
      changeYear.tillageId = SOIL_SIGHT_TILLAGE.REDUCED;
    } else if (year.tillageId === SOIL_SIGHT_TILLAGE.REDUCED) {
      changeYear.tillageId = SOIL_SIGHT_TILLAGE.NONE;
    }

    const hasNoCoverCrop = !growsCoverCrop(year);
    const harvestDate = slotDates[slotIndex]?.harvestDate;
    const nextSeedingDate = slotDates[slotIndex + 1]?.seedingDate;
    if (hasNoCoverCrop && harvestDate && nextSeedingDate) {
      const recommendedCoverCropId = recommendCoverCrop(harvestDate, nextSeedingDate);
      if (recommendedCoverCropId) {
        changeYear.coverCropId = recommendedCoverCropId;
      }
    } else if (year.coverCropId === COVER_CROP_MIX_TYPE.SUMMER_SINGLE_SPECIES) {
      // An existing single-species mix becomes the multi-species mix of the same season.
      changeYear.coverCropId = COVER_CROP_MIX_TYPE.SUMMER_MULTI_SPECIES;
    } else if (year.coverCropId === COVER_CROP_MIX_TYPE.WINTER_SINGLE_SPECIES) {
      changeYear.coverCropId = COVER_CROP_MIX_TYPE.WINTER_MULTI_SPECIES;
    }

    // The opt-in practice keeps the baseline value until the user switches it on.
    initialChangeYears.push(IS_PRACTICE_APPLIED_BY_DEFAULT.coverCrop ? changeYear : { ...changeYear, coverCropId: year.coverCropId });

    return changeYear;
  });

  return { changeYears, initialChangeYears };
};
